import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, TrendingUp, AlertCircle } from "lucide-react";

interface TeamMember {
  userId: string;
  name: string;
  email: string;
  role: string;
  isCreator: boolean;
  topStrengths: { rank: number; name: string; nameAr: string; domain: string; description: string }[];
}

interface SharedStrength {
  name: string;
  nameAr: string;
  domain: string;
  count: number;
  totalMembers: number;
  members: { username: string; rank: number }[];
}

interface DomainCoverage {
  domain: string;
  userCount: number;
}

interface TeamComparisonProps {
  teamMembers: TeamMember[];
  sharedStrengths?: SharedStrength[];
  domainCoverage?: DomainCoverage[];
  showTopStrengths?: number;
}

export default function TeamComparison({
  teamMembers,
  sharedStrengths = [],
  domainCoverage = [],
  showTopStrengths = 10,
}: TeamComparisonProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [selectedMembers, setSelectedMembers] = useState<string[]>(
    teamMembers.map(m => m.userId)
  );

  const getDomainColor = (domain: string) => {
    switch (domain) {
      case "Strategic Thinking":
        return "bg-primary/10 text-primary border-primary/25";
      case "Executing":
        return "bg-secondary/10 text-brand-green-dark border-secondary/25";
      case "Influencing":
        return "bg-warning/10 text-warning border-warning/25";
      case "Relationship Building":
        return "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25";
      default:
        return "bg-secondary/20 text-secondary border-secondary/30";
    }
  };

  const toggleMember = (memberId: string) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const filteredMembers = teamMembers.filter(m => selectedMembers.includes(m.userId));
  
  // Filter shared strengths based on selected members
  const filteredSharedStrengths = sharedStrengths.filter(strength => 
    strength.members.some(member => 
      filteredMembers.some(fm => fm.name === member.username)
    )
  ).slice(0, showTopStrengths); // Show top N shared strengths

  return (
    <div className="space-y-6 text-start" dir={isRtl ? "rtl" : "ltr"}>
      {/* Member Selection */}
      <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          {t('team.selectMembers')}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          {teamMembers.map(member => (
            <div
              key={member.userId}
              className="flex min-w-0 items-center gap-2 rounded-lg border border-border p-3 transition-colors hover:border-primary/50"
            >
              <Checkbox
                id={member.userId}
                checked={selectedMembers.includes(member.userId)}
                onCheckedChange={() => toggleMember(member.userId)}
              />
              <label
                htmlFor={member.userId}
                className="min-w-0 cursor-pointer break-words text-sm font-medium leading-none"
              >
                {member.name}
              </label>
            </div>
          ))}
        </div>
      </Card>

      {/* Comparison Matrix */}
      {filteredMembers.length > 0 && (
        <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t('team.comparisonMatrix')}
          </h3>
          <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          <div className="min-w-[520px] sm:min-w-[600px]">
            <div className="grid gap-3 sm:gap-4" style={{ gridTemplateColumns: `minmax(120px, 200px) repeat(${filteredMembers.length}, minmax(120px, 1fr))` }}>
              {/* Header */}
              <div className="font-semibold text-muted-foreground">
                {t('team.rank')}
              </div>
              {filteredMembers.map(member => (
                <div key={member.userId} className="break-words text-center font-semibold">
                  {member.name}
                </div>
              ))}

              {/* Rows for each rank */}
              {Array.from({ length: showTopStrengths }).map((_, rankIndex) => (
                <div key={rankIndex} className="contents">
                  <div className="flex items-center gap-2 py-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">
                        #{rankIndex + 1}
                      </span>
                    </div>
                  </div>
                  {filteredMembers.map(member => {
                    const strength = member.topStrengths[rankIndex];
                    return strength ? (
                      <div key={`${member.userId}-${rankIndex}`} className="py-2 text-center">
                        <div className="mb-1 break-words text-sm font-medium">{strength.name}</div>
                        <Badge className={`text-xs pointer-events-none ${getDomainColor(strength.domain)}`}>
                          {strength.domain}
                        </Badge>
                      </div>
                    ) : (
                      <div key={`${member.userId}-${rankIndex}`} className="py-2 text-center text-muted-foreground">
                        -
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          </div>
        </Card>
      )}

      {/* Insights */}
      {filteredMembers.length > 1 && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Shared Strengths */}
          <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              {t('team.sharedStrengths')}
            </h3>
            {filteredSharedStrengths.length > 0 ? (
              <div className="space-y-3">
                {filteredSharedStrengths.map((strength, index) => (
                  <div key={`${strength.name}-${index}`} className="p-3 bg-accent/5 rounded-lg border border-accent/20">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <span className="min-w-0 break-words font-medium">{strength.name}</span>
                      <Badge className="bg-accent/20 text-accent pointer-events-none">
                        {strength.members.length}/{filteredMembers.length}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      {strength.members.map((m, idx) => m.username).join(", ")}
                    </div>
                    <Badge className={`text-xs pointer-events-none ${getDomainColor(strength.domain)}`}>
                      {strength.domain}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('team.noSharedStrengths')}
              </p>
            )}
          </Card>

          {/* Domain Coverage */}
          <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-primary" />
              {t('team.domainCoverage')}
            </h3>
            <div className="space-y-3">
              {domainCoverage.map((domain, index) => (
                <div key={domain.domain} className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge className={`pointer-events-none ${getDomainColor(domain.domain)}`}>
                      {domain.domain}
                    </Badge>
                    <span className="text-sm font-medium">
                      {domain.userCount} {t('team.members')}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${(domain.userCount / (filteredMembers.length * showTopStrengths)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
